import React, { useState, useEffect } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import ProgressBar from './ProgressBar';
import BrutalistButton from './BrutalistButton';
import { 
  fillerWordsData, 
  clarityCompassData, 
  optionalToolsData, 
  dialogueTightenerData 
} from '../data/checklistData';

export default function Pass2Tab({ state, setState }) {
  const [expandedId, setExpandedId] = useState(null);

  // Robust initialization of clarity chapters
  const clarityChapters = state.clarityChapters && state.clarityChapters.length > 0 
    ? state.clarityChapters 
    : [];

  useEffect(() => {
    if (clarityChapters.length > 0 && !expandedId) {
      setExpandedId(clarityChapters[0].id);
    } else if (clarityChapters.length === 0) {
      const defaultChapters = Array.from({ length: 3 }, (_, i) => ({
        id: `clarity-${Date.now()}-${i}`,
        title: `Chapter/Scene ${i + 1}`,
        unclear: '',
        choppy: '',
        tone: '',
        repetitive: ''
      }));
      setState(prev => ({ ...prev, clarityChapters: defaultChapters }));
      setExpandedId(defaultChapters[0].id);
    }
  }, []);

  const updateField = (field, value) => {
    setState(prev => ({ ...prev, [field]: value }));
  };

  const updateClarityChapterField = (id, field, value) => {
    setState(prev => ({
      ...prev,
      clarityChapters: prev.clarityChapters.map(ch => ch.id === id ? { ...ch, [field]: value } : ch)
    }));
  };

  const handleAddClarityChapter = () => {
    const newId = `clarity-${Date.now()}`;
    const newChapter = {
      id: newId,
      title: `Chapter/Scene ${clarityChapters.length + 1}`,
      unclear: '',
      choppy: '',
      tone: '',
      repetitive: ''
    };
    setState(prev => ({ ...prev, clarityChapters: [...(prev.clarityChapters || []), newChapter] }));
    setExpandedId(newId);
  };

  const handleRemoveClarityChapter = (e, idToRemove) => {
    e.stopPropagation();
    if(window.confirm('Remove this clarity assessment?')) {
      const filtered = clarityChapters.filter(ch => ch.id !== idToRemove);
      const renumbered = filtered.map((ch, idx) => ({ 
        ...ch, 
        title: `Chapter/Scene ${idx + 1}` 
      }));
      setState(prev => ({ ...prev, clarityChapters: renumbered }));
      if (expandedId === idToRemove && renumbered.length > 0) {
        setExpandedId(renumbered[0].id);
      }
    }
  };

  const handleClear = () => {
    if(window.confirm('Clear all Pass 2 progress?')) {
      const resetClarity = Array.from({ length: 3 }, (_, i) => ({
        id: `clarity-reset-${i}`,
        title: `Chapter/Scene ${i + 1}`,
        unclear: '',
        choppy: '',
        tone: '',
        repetitive: ''
      }));
      setState({
        tool1_identified: '',
        tool1_reduce: '',
        clarityChapters: resetClarity,
        opt_paragraphs: '',
        opt_dialogue: '',
        opt_readaloud: '',
        plan_issue1: '', plan_issue2: '', plan_issue3: '',
        plan_goal1: '', plan_goal2: '', plan_goal3: ''
      });
      setExpandedId(resetClarity[0].id);
    }
  };

  // Calculate Progress
  const baseFields = ['tool1_identified', 'tool1_reduce', 'opt_paragraphs', 'opt_dialogue', 'opt_readaloud', 'plan_issue1', 'plan_goal1'];
  const completedBase = baseFields.filter(f => state[f]?.trim().length > 0).length;
  const completedClarity = clarityChapters.filter(ch => 
    ch.unclear.trim().length > 0 || ch.choppy.trim().length > 0
  ).length;
  const progress = Math.min(100, ((completedBase + completedClarity) / (baseFields.length + 3)) * 100);

  const BrutalistList = ({ title, items, colorClass = "border-black" }) => (
    <div className={`p-5 bg-white border-2 ${colorClass} h-full shadow-brutal-sm`}>
      <h4 style={{
      fontSize: '16px'
    }} className="font-bold text-[13px] uppercase tracking-wider mb-3 text-gray-500">{title}</h4>
      <ul className="space-y-2">
        {items.map((item, idx) => (
          <li key={idx} className="flex items-start gap-2 text-sm text-gray-800">
            <span className="mt-1.5 w-1.5 h-1.5 bg-black flex-shrink-0" />
            <span style={{
      fontSize: '15px'
    }} className="leading-snug font-medium">{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <div className="animate-fade-in max-w-4xl">
      <div className="mb-8">
        <h2 className="text-2xl font-bold uppercase tracking-tight mb-2">
          Pass 2 Tools: Micro Editing & Clarity
        </h2>
        <p className="text-[15px] text-gray-600 leading-relaxed border-l-4 border-blue pl-4 bg-white p-3 shadow-brutal-sm">
          Refine clarity, flow, tone, and readability at the sentence and paragraph level.
        </p>
      </div>

      <ProgressBar percentage={progress} passName="Micro Editing Progress" />

      {/* TOOL 1: FILLER WORD & CLUTTER LIST */}
      <div className="mt-12 mb-16">
        <h3 className="font-bold text-xl uppercase tracking-wide mb-2 pb-2 border-b-2 border-blue inline-block">
          Tool 1: Filler Word & Clutter List
        </h3>
        <p className="text-sm text-gray-600 mb-8 italic">Search and review these common filler words to evaluate clarity.</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          {fillerWordsData.map((section, idx) => (
            <BrutalistList key={idx} title={section.title} items={section.items} colorClass="border-gray-300" />
          ))}
        </div>

        <div className="space-y-6 bg-offwhite p-6 border-2 border-black shadow-brutal-sm">
          <div>
            <label style={{
      fontSize: '15px'
    }} className="block font-bold text-sm mb-2">Words I identified repeatedly in my manuscript:</label>
            <textarea 
              className="w-full border-2 border-gray-400 focus:border-blue p-3 text-sm focus:outline-none transition-colors resize-y min-h-[80px]"
              value={state.tool1_identified || ''}
              onChange={(e) => updateField('tool1_identified', e.target.value)}
              placeholder="e.g., I noticed I use 'just' and 'actually' way too much..."
            />
          </div>
          <div>
            <label className="block font-bold text-sm mb-2">Words I need to intentionally reduce or replace:</label>
            <textarea 
              className="w-full border-2 border-gray-400 focus:border-blue p-3 text-sm focus:outline-none transition-colors resize-y min-h-[80px]"
              value={state.tool1_reduce || ''}
              onChange={(e) => updateField('tool1_reduce', e.target.value)}
              placeholder="e.g., Replace 'began to walk' with 'marched'..."
            />
          </div>
        </div>
      </div>

      {/* TOOL 2: CLARITY COMPASS */}
      <div className="mt-16 mb-16">
        <h3 className="font-bold text-xl uppercase tracking-wide mb-2 pb-2 border-b-2 border-blue inline-block">
          Tool 2: The "Clarity Compass"
        </h3>
        
        <div className="mb-8">
          <p style={{
      fontSize: '16px'
    }} className="text-[15px] font-bold text-gray-800 mb-4 bg-blue/5 p-4 border-l-4 border-blue">Use these four prompts to bring clarity to your book as you go about your self-edit:</p>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-8 list-none pl-1">
            <li style={{
      color: 'rgb(55, 65, 81)',
      fontSize: '14px',
      fontWeight: '700'
    }} className="flex items-center gap-3 text-sm font-bold text-gray-700">
              <div className="w-1.5 h-1.5 bg-blue rounded-full" />Sentences that felt unclear or too long</li>
            <li style={{
      color: 'rgb(55, 65, 81)',
      fontSize: '14px'
    }} className="flex items-center gap-3 text-sm font-bold text-gray-700">
              <div className="w-1.5 h-1.5 bg-blue rounded-full" />Sections where flow felt choppy</li>
            <li style={{
      color: 'rgb(55, 65, 81)',
      fontSize: '14px'
    }} className="flex items-center gap-3 text-sm font-bold text-gray-700">
              <div className="w-1.5 h-1.5 bg-blue rounded-full" />Tone issues (too formal, abrupt, preachy)</li>
            <li style={{
      color: 'rgb(55, 65, 81)'
    }} className="flex items-center gap-3 text-sm font-bold text-gray-700">
              <div className="w-1.5 h-1.5 bg-blue rounded-full" />Repetitive words/phrases identified</li>
          </ul>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
          {clarityCompassData.slice(0, 4).map((section, idx) => (
            <BrutalistList key={idx} title={section.title} items={section.items} colorClass="border-gray-200" />
          ))}
        </div>

        {/* Dynamic Clarity Chapters */}
        <div className="space-y-4">
          {clarityChapters.map((chapter) => {
            const isExpanded = expandedId === chapter.id;
            return (
              <div key={chapter.id} className="border-2 border-black bg-white shadow-brutal-sm overflow-hidden transition-all">
                <div 
                  onClick={() => setExpandedId(isExpanded ? null : chapter.id)}
                  className={`flex justify-between items-center p-4 cursor-pointer transition-colors ${isExpanded ? 'bg-black text-white' : 'bg-white text-black hover:bg-gray-50'}`}
                >
                  <div className="flex items-center gap-4">
                    <SafeIcon 
                      icon={isExpanded ? FiIcons.FiChevronDown : FiIcons.FiChevronRight} 
                      className={`text-xl ${isExpanded ? 'text-blue' : 'text-gray-400'}`}
                    />
                    <h4 className="font-bold text-sm uppercase tracking-wider">{chapter.title}</h4>
                  </div>
                  {clarityChapters.length > 1 && (
                    <button 
                      onClick={(e) => handleRemoveClarityChapter(e, chapter.id)}
                      className={`p-1 hover:bg-red-500 hover:text-white rounded transition-colors ${isExpanded ? 'text-gray-500' : 'text-gray-300'}`}
                    >
                      <SafeIcon icon={FiIcons.FiTrash2} />
                    </button>
                  )}
                </div>

                {isExpanded && (
                  <div className="p-6 animate-slide-down border-t-2 border-black space-y-6 bg-offwhite">
                    <div>
                      <label style={{
      fontSize: '14px'
    }} className="block font-bold text-xs uppercase tracking-widest mb-2 text-blue">1. Sentences that felt unclear or too long:</label>
                      <textarea 
                        className="w-full border-2 border-gray-300 focus:border-blue p-3 text-sm focus:outline-none bg-white transition-colors min-h-[80px]"
                        value={chapter.unclear}
                        onChange={(e) => updateClarityChapterField(chapter.id, 'unclear', e.target.value)}
                        placeholder="List specific sentences to shorten or clarify..."
                      />
                    </div>
                    <div>
                      <label style={{
      fontSize: '14px'
    }} className="block font-bold text-xs uppercase tracking-widest mb-2 text-blue">2. Sections where flow felt choppy:</label>
                      <textarea 
                        className="w-full border-2 border-gray-300 focus:border-blue p-3 text-sm focus:outline-none bg-white transition-colors min-h-[80px]"
                        value={chapter.choppy}
                        onChange={(e) => updateClarityChapterField(chapter.id, 'choppy', e.target.value)}
                        placeholder="Note transitions that need softening..."
                      />
                    </div>
                    <div>
                      <label style={{
      fontSize: '14px'
    }} className="block font-bold text-xs uppercase tracking-widest mb-2 text-blue">3. Tone issues detected:</label>
                      <textarea 
                        className="w-full border-2 border-gray-300 focus:border-blue p-3 text-sm focus:outline-none bg-white transition-colors min-h-[80px]"
                        value={chapter.tone}
                        onChange={(e) => updateClarityChapterField(chapter.id, 'tone', e.target.value)}
                        placeholder="Too formal? Too abrupt? Too preachy?..."
                      />
                    </div>
                    <div>
                      <label style={{
      fontSize: '14px'
    }} className="block font-bold text-xs uppercase tracking-widest mb-2 text-blue">4. Repetitive words/phrases identified:</label>
                      <textarea 
                        className="w-full border-2 border-gray-300 focus:border-blue p-3 text-sm focus:outline-none bg-white transition-colors min-h-[80px]"
                        value={chapter.repetitive}
                        onChange={(e) => updateClarityChapterField(chapter.id, 'repetitive', e.target.value)}
                        placeholder="Which words did you use too many times here?..."
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <button 
          onClick={handleAddClarityChapter}
          className="mt-6 flex items-center justify-center gap-2 w-full py-4 border-2 border-dashed border-gray-400 text-gray-500 hover:border-black hover:text-black hover:bg-white transition-all font-bold uppercase tracking-wider text-sm"
        >
          <SafeIcon icon={FiIcons.FiPlus} /> Add Another Chapter/Scene Clarity Check
        </button>
      </div>

      {/* OPTIONAL TOOLS */}
      <div className="mt-16 mb-16 space-y-10">
        <h3 className="font-bold text-xl uppercase tracking-wide mb-8 pb-2 border-b-2 border-black inline-block">Optional Micro Editing Steps</h3>
        {optionalToolsData.map((tool) => (
          <div key={tool.id} className="border-2 border-black bg-white flex flex-col md:flex-row shadow-brutal-sm">
            <div className="md:w-1/3 border-b-2 md:border-b-0 md:border-r-2 border-black p-5 bg-gray-50">
              <h4 className="font-bold text-[15px] uppercase tracking-wide mb-2 text-blue">{tool.title}</h4>
              <p className="text-xs text-gray-600 mb-3 italic">{tool.description}</p>
              <ul className="space-y-2">
                {tool.items.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-gray-800">
                    <span className="mt-1.5 w-1.5 h-1.5 bg-black flex-shrink-0" />
                    <span className="leading-snug text-xs font-medium">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="md:w-2/3 p-5">
              <label className="block font-bold text-sm mb-3">{tool.prompt}</label>
              <textarea 
                className="w-full border-2 border-gray-300 focus:border-blue p-3 text-sm focus:outline-none transition-colors min-h-[120px]"
                value={state[tool.id] || ''}
                onChange={(e) => updateField(tool.id, e.target.value)}
              />
            </div>
          </div>
        ))}
      </div>

      {/* REVISION PLAN */}
      <div className="mt-16 mb-12 border-t-4 border-black pt-10">
        <h3 className="font-bold text-2xl uppercase tracking-wide mb-8 text-center text-blue">Micro Editing Revision Plan</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-white border-2 border-black p-6 shadow-brutal-sm">
            <h4 className="font-bold uppercase tracking-wide mb-6 text-orange border-b-2 border-gray-100 pb-2">Top 3 Issues Identified</h4>
            <div className="space-y-4">
              {[1, 2, 3].map((num) => (
                <div key={num} className="flex items-center gap-3">
                  <span className="font-bold text-lg text-gray-300">{num}.</span>
                  <input 
                    type="text" 
                    className="w-full border-b-2 border-gray-200 focus:border-black py-2 text-sm focus:outline-none transition-colors bg-transparent"
                    value={state[`plan_issue${num}`] || ''}
                    onChange={(e) => updateField(`plan_issue${num}`, e.target.value)}
                    placeholder="Enter issue..."
                  />
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white border-2 border-black p-6 shadow-brutal-sm">
            <h4 className="font-bold uppercase tracking-wide mb-6 text-blue border-b-2 border-gray-100 pb-2">Goals for This Week</h4>
            <div className="space-y-4">
              {[1, 2, 3].map((num) => (
                <div key={num} className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-black flex-shrink-0" />
                  <input 
                    type="text" 
                    className="w-full border-b-2 border-gray-200 focus:border-black py-2 text-sm focus:outline-none transition-colors bg-transparent"
                    value={state[`plan_goal${num}`] || ''}
                    onChange={(e) => updateField(`plan_goal${num}`, e.target.value)}
                    placeholder="Enter goal..."
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-12 flex justify-start border-t-2 border-black pt-8">
        <BrutalistButton onClick={handleClear} variant="secondary">Clear All Pass 2 Progress</BrutalistButton>
      </div>
    </div>
  );
}